import React from 'react';
import {compose, graphql} from 'react-apollo';
import gql from 'graphql-tag';
import uuid from 'uuid/v4';
import {Redirect} from '@reach/router';

import {Card, Button, Loading} from 'ui-kit';

import {MemberFields} from './components';

import * as styles from './NewMembership.module.css';

function createNewMember() {
  return {
    id: uuid(),
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    dobDay: '1',
    dobMonth: '0',
    dobYear: '1990',
  };
}

function memberReducer(members, action) {
  const {payload = {}} = action;
  const memberIndex = payload.id
    ? members.findIndex(member => member.id === payload.id)
    : null;

  switch (action.type) {
    case 'update':
      return [
        ...members.slice(0, memberIndex),
        {
          id: payload.id,
          ...payload.member,
        },
        ...members.slice(memberIndex + 1),
      ];
    case 'add':
      return [...members, createNewMember()];
    case 'remove':
      return [
        ...members.slice(0, memberIndex),
        ...members.slice(memberIndex + 1),
      ];
    default:
      throw new Error(`Unexpected action type '${action.type}'`);
  }
}

function NewMembership({mutate, stripe}) {
  const [members, dispatch] = React.useReducer(memberReducer, [
    createNewMember(),
  ]);
  const [loading, setLoading] = React.useState(false);
  const [, setErrors] = React.useState({});

  const [successMembershipId, setSuccessMembershipId] = React.useState(null);

  if (successMembershipId != null) {
    return <Redirect to={`/memberships/${successMembershipId}`} noThrow />;
  }

  async function handleSubmit(event) {
    event.preventDefault();

    setLoading(true);

    try {
      const response = await mutate({
        variables: {
          input: {
            members: members.map((member, index) => ({
              firstName: member.firstName,
              lastName: member.lastName,
              dateOfBirth: new Date(
                Date.UTC(member.dobYear, member.dobMonth, member.dobDay),
              ).toISOString(),
              email: member.email,
              phone: member.phone,
              primaryAccountHolder: index === 0,
            })),
          },
        },
      });

      const {data} = response;
      const {
        createMembershipV2: {errors, membership},
      } = data;

      if (errors.length > 0) {
        setLoading(false);

        const clientErrors = errors.reduce((allErrors, {path, message}) => {
          if (allErrors[path] == null) {
            return {
              ...allErrors,
              [path]: message,
            };
          }

          return {
            ...allErrors,
            [path]: `${allErrors[path]}; ${message}`,
          };
        }, {});

        setErrors(clientErrors);
      } else {
        setSuccessMembershipId(membership.id);
      }
    } catch (error) {
      setLoading(false);
      console.log(error);
    }
  }

  return (
    <div className={styles.Container}>
      <Card>
        <form method="post" onSubmit={handleSubmit}>
          <h2 className={styles.SectionHeading}>Members</h2>

          {members.map((member, index) => {
            return (
              <div className={styles.MemberContainer} key={member.id}>
                {index === 0 ? (
                  <p className={styles.PrimaryAccountHolderText}>
                    <strong>Primary account holder</strong>
                  </p>
                ) : (
                  <div className={styles.AdditionalMemberHeader}>
                    <p className={styles.AdditionalMemberTitle}>
                      <strong>Additional member {index + 1}</strong>
                    </p>
                    <button
                      type="button"
                      onClick={() =>
                        dispatch({type: 'remove', payload: {id: member.id}})
                      }
                      className={styles.AdditionalMemberRemove}
                    >
                      Remove
                    </button>
                  </div>
                )}

                <MemberFields
                  member={member}
                  onChange={newMember =>
                    dispatch({
                      type: 'update',
                      payload: {
                        id: member.id,
                        member: newMember,
                      },
                    })
                  }
                />
              </div>
            );
          })}

          <div className={styles.AddButtonContainer}>
            <Button
              color="light"
              onClick={() => dispatch({type: 'add'})}
              type="button"
            >
              + Add a member
            </Button>
          </div>

          <Button type="submit" disabled={loading}>
            {loading ? <Loading immediate /> : 'Create membership'}
          </Button>
        </form>
      </Card>
    </div>
  );
}

export default compose(
  graphql(
    gql`
      mutation NewMembershipMutation($input: CreateMembershipV2Input!) {
        createMembershipV2(input: $input) {
          errors {
            message
            path
          }
          membership {
            id
            memberV2s {
              id
            }
          }
        }
      }
    `,
  ),
)(NewMembership);
